import { parseRadarState, type RadarStateResponse } from "./api";
import type { RadarConnection } from "./model";

export type ServerVersion = {
  gitCommit: string;
  cloudflareWorkerVersion: string;
};

type SocketEvent = Event & { data?: unknown };

export type RadarSocket = {
  readonly readyState: number;
  addEventListener(type: "open" | "close" | "error" | "message", listener: (event: SocketEvent) => void): void;
  close(): void;
  send(data: string): void;
};

export type RadarConnectionEnvironment = {
  isOnline(): boolean;
  isVisible(): boolean;
  addWindowListener(type: "online" | "offline" | "focus" | "pageshow", listener: () => void): void;
  removeWindowListener(type: "online" | "offline" | "focus" | "pageshow", listener: () => void): void;
  addVisibilityListener(listener: () => void): void;
  removeVisibilityListener(listener: () => void): void;
};

export type RadarConnectionOptions = {
  createSocket: () => RadarSocket;
  environment: RadarConnectionEnvironment;
  onConnection: (state: RadarConnection) => void;
  onState: (state: RadarStateResponse) => void;
  onVersion: (version: ServerVersion) => void;
  reconcile: () => void | Promise<void>;
  now?: () => number;
  random?: () => number;
  setTimeout?: typeof globalThis.setTimeout;
  clearTimeout?: typeof globalThis.clearTimeout;
  openTimeoutMs?: number;
  pingIntervalMs?: number;
  pongTimeoutMs?: number;
  sleepGapMs?: number;
  retryBaseMs?: number;
  retryMaxMs?: number;
  retryJitter?: number;
  failureReconcileIntervalMs?: number;
};

type Timer = ReturnType<typeof globalThis.setTimeout>;
const SOCKET_OPEN = 1;

export class RadarConnectionManager {
  private readonly now: () => number;
  private readonly random: () => number;
  private readonly schedule: typeof globalThis.setTimeout;
  private readonly cancel: typeof globalThis.clearTimeout;
  private active = false;
  private generation = 0;
  private socket: RadarSocket | null = null;
  private retryAttempt = 0;
  private retryTimer: Timer | null = null;
  private openTimer: Timer | null = null;
  private heartbeatTimer: Timer | null = null;
  private pongTimer: Timer | null = null;
  private lastSchedulerTick: number | null = null;
  private lastFailureReconcileAt: number | null = null;
  private supportsPong = false;
  private reconcileInFlight = false;
  private reconcileQueued = false;

  constructor(private readonly options: RadarConnectionOptions) {
    this.now = options.now ?? Date.now;
    this.random = options.random ?? Math.random;
    this.schedule = options.setTimeout ?? globalThis.setTimeout.bind(globalThis);
    this.cancel = options.clearTimeout ?? globalThis.clearTimeout.bind(globalThis);
  }

  start(): void {
    if (this.active) return;
    this.active = true;
    this.lastSchedulerTick = this.now();
    this.lastFailureReconcileAt = null;
    this.options.environment.addWindowListener("online", this.handleOnline);
    this.options.environment.addWindowListener("offline", this.handleOffline);
    this.options.environment.addWindowListener("focus", this.handleRecoverySignal);
    this.options.environment.addWindowListener("pageshow", this.handleRecoverySignal);
    this.options.environment.addVisibilityListener(this.handleVisibility);
    if (!this.options.environment.isOnline()) {
      this.options.onConnection("offline");
      return;
    }
    this.options.onConnection("reconnecting");
    this.connectNow();
  }

  stop(): void {
    if (!this.active) return;
    this.active = false;
    this.options.environment.removeWindowListener("online", this.handleOnline);
    this.options.environment.removeWindowListener("offline", this.handleOffline);
    this.options.environment.removeWindowListener("focus", this.handleRecoverySignal);
    this.options.environment.removeWindowListener("pageshow", this.handleRecoverySignal);
    this.options.environment.removeVisibilityListener(this.handleVisibility);
    this.invalidateSocket();
    this.clearTimer("retryTimer");
  }

  recover(reconcile = true): void {
    if (!this.active || !this.options.environment.isOnline()) return;
    this.clearTimer("retryTimer");
    if (reconcile) this.requestReconcile();
    const schedulerGap = this.lastSchedulerTick !== null &&
      this.now() - this.lastSchedulerTick > (this.options.sleepGapMs ?? 60_000);
    if (schedulerGap && this.options.environment.isVisible()) {
      this.options.onConnection("reconnecting");
      this.connectNow();
      return;
    }
    if (!this.socket || this.socket.readyState !== SOCKET_OPEN) {
      this.options.onConnection("reconnecting");
      this.connectNow();
      return;
    }
    if (this.options.environment.isVisible() && !this.heartbeatTimer) this.scheduleHeartbeat();
  }

  private readonly handleOnline = () => {
    this.recover(true);
  };

  private readonly handleOffline = () => {
    if (!this.active) return;
    this.invalidateSocket();
    this.clearTimer("retryTimer");
    this.options.onConnection("offline");
  };

  private readonly handleRecoverySignal = () => this.recover(true);

  private readonly handleVisibility = () => {
    if (!this.active) return;
    if (!this.options.environment.isVisible()) {
      this.clearLivenessTimers();
      return;
    }
    this.recover(true);
  };

  private connectNow(): void {
    if (!this.active || !this.options.environment.isOnline()) return;
    this.invalidateSocket();
    this.supportsPong = false;
    const generation = this.generation;
    let socket: RadarSocket;
    try {
      socket = this.options.createSocket();
    } catch {
      this.failGeneration(generation);
      return;
    }
    if (!this.active || generation !== this.generation) {
      socket.close();
      return;
    }
    this.socket = socket;
    socket.addEventListener("open", () => this.handleOpen(generation));
    socket.addEventListener("message", (event) => this.handleMessage(generation, event.data));
    socket.addEventListener("error", () => this.failGeneration(generation));
    socket.addEventListener("close", () => this.failGeneration(generation));
    this.openTimer = this.schedule(() => this.failGeneration(generation), this.options.openTimeoutMs ?? 10_000);
  }

  private handleOpen(generation: number): void {
    if (!this.isCurrent(generation)) return;
    this.clearTimer("openTimer");
    this.options.onConnection("connected");
    if (this.options.environment.isVisible() && this.options.environment.isOnline()) this.scheduleHeartbeat();
  }

  private handleMessage(generation: number, data: unknown): void {
    if (!this.isCurrent(generation)) return;
    let message: unknown;
    try {
      message = JSON.parse(String(data));
    } catch {
      this.failGeneration(generation);
      return;
    }
    if (!isRecord(message)) {
      this.failGeneration(generation);
      return;
    }
    const version = readVersion(message);
    if (version) {
      this.supportsPong = true;
      this.options.onVersion(version);
    }
    if (message.type === "pong") {
      this.clearTimer("pongTimer");
      this.retryAttempt = 0;
      return;
    }
    if (message.type === "state" && "state" in message) {
      const state = parseRadarState(message.state);
      if (!state) {
        this.failGeneration(generation);
        return;
      }
      this.retryAttempt = 0;
      this.options.onState(state);
      return;
    }
    if (message.type === "state") this.failGeneration(generation);
  }

  private scheduleHeartbeat(): void {
    this.clearTimer("heartbeatTimer");
    this.lastSchedulerTick = this.now();
    this.heartbeatTimer = this.schedule(this.runHeartbeat, this.options.pingIntervalMs ?? 25_000);
  }

  private readonly runHeartbeat = () => {
    this.heartbeatTimer = null;
    if (!this.active || !this.options.environment.isOnline() || !this.options.environment.isVisible()) return;
    const currentTime = this.now();
    if (this.lastSchedulerTick !== null &&
        currentTime - this.lastSchedulerTick > (this.options.sleepGapMs ?? 60_000)) {
      this.requestReconcile();
      this.options.onConnection("reconnecting");
      this.connectNow();
      return;
    }
    this.lastSchedulerTick = currentTime;
    const generation = this.generation;
    try {
      this.socket?.send(JSON.stringify({ type: "ping" }));
    } catch {
      this.failGeneration(generation);
      return;
    }
    this.clearTimer("pongTimer");
    if (this.supportsPong) {
      this.pongTimer = this.schedule(() => this.failGeneration(generation), this.options.pongTimeoutMs ?? 10_000);
    }
    this.heartbeatTimer = this.schedule(this.runHeartbeat, this.options.pingIntervalMs ?? 25_000);
  };

  private failGeneration(generation: number): void {
    if (!this.isCurrent(generation)) return;
    this.invalidateSocket();
    if (!this.active) return;
    if (!this.options.environment.isOnline()) {
      this.options.onConnection("offline");
      return;
    }
    const now = this.now();
    if (this.lastFailureReconcileAt === null ||
        now - this.lastFailureReconcileAt >= (this.options.failureReconcileIntervalMs ?? 30_000)) {
      this.lastFailureReconcileAt = now;
      this.requestReconcile();
    }
    this.options.onConnection("reconnecting");
    const base = this.options.retryBaseMs ?? 1_000;
    const maximum = this.options.retryMaxMs ?? 30_000;
    const jitter = this.options.retryJitter ?? 0.25;
    const exponential = Math.min(maximum, base * (2 ** Math.min(this.retryAttempt, 30)));
    this.retryAttempt += 1;
    const delay = exponential * (1 - jitter + (2 * jitter * this.random()));
    this.retryTimer = this.schedule(() => {
      this.retryTimer = null;
      this.connectNow();
    }, delay);
  }

  private invalidateSocket(): void {
    this.generation += 1;
    const socket = this.socket;
    this.socket = null;
    this.clearTimer("openTimer");
    this.clearLivenessTimers();
    if (socket) {
      try { socket.close(); } catch { /* A failed close is already disconnected locally. */ }
    }
  }

  private requestReconcile(): void {
    if (!this.active) return;
    if (this.reconcileInFlight) {
      this.reconcileQueued = true;
      return;
    }
    this.reconcileInFlight = true;
    void Promise.resolve(this.options.reconcile()).catch(() => undefined).finally(() => {
      this.reconcileInFlight = false;
      if (!this.active || !this.reconcileQueued) return;
      this.reconcileQueued = false;
      this.requestReconcile();
    });
  }

  private clearLivenessTimers(): void {
    this.clearTimer("heartbeatTimer");
    this.clearTimer("pongTimer");
  }

  private clearTimer(key: "retryTimer" | "openTimer" | "heartbeatTimer" | "pongTimer"): void {
    const timer = this[key];
    if (timer !== null) this.cancel(timer);
    this[key] = null;
  }

  private isCurrent(generation: number): boolean {
    return this.active && generation === this.generation;
  }
}

export function browserRadarConnectionEnvironment(): RadarConnectionEnvironment {
  return {
    isOnline: () => navigator.onLine,
    isVisible: () => document.visibilityState === "visible",
    addWindowListener: (type, listener) => window.addEventListener(type, listener),
    removeWindowListener: (type, listener) => window.removeEventListener(type, listener),
    addVisibilityListener: (listener) => document.addEventListener("visibilitychange", listener),
    removeVisibilityListener: (listener) => document.removeEventListener("visibilitychange", listener)
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readVersion(message: Record<string, unknown>): ServerVersion | null {
  const candidate = isRecord(message.version) ? message.version : message;
  return typeof candidate.gitCommit === "string" && typeof candidate.cloudflareWorkerVersion === "string"
    ? { gitCommit: candidate.gitCommit, cloudflareWorkerVersion: candidate.cloudflareWorkerVersion }
    : null;
}
